import * as React from 'react';
import * as CertificationWatermark from '@tableau/certification-watermark';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import { IColumnFactoryParameters } from '../ListViewGridColumnGenerator';
import * as Layout from '../../../../common/styles/Layout';
import { LinkCell } from './LinkCell';
import { IconStyle } from '../../../utils/DataCatalogUtils';
import { ContentTypeCell, ContentTypeCellOptions } from './ContentTypeCell';
import { CertificationCell, IDataQualityIndicatorInfo } from './CertificationCell';
import { IVirtualConnection } from '../../../models/httpClient/MetaDataResults';
import { AppUrls } from '../../../../app/AppUrls';
import { UserMapper } from '../../../../common/utils/UserMapper';
import { HiddenUserStyles } from './DataSourceType';
import { MapperHelper } from '../../../../common/utils/MapperHelper';
import { DataQualityWarningCell } from './DataQualityWarningCell';

export function VirtualConnectionNameCell(
  parameters: IColumnFactoryParameters,
  contentTypeCellOptions: ContentTypeCellOptions
): React.Factory<BodyCell.Props> {
  return React.createFactory(function TableNameCell(props: BodyCell.Props) {
    const contentTypeCellFactory = ContentTypeCell(contentTypeCellOptions);
    const certificationFactory = CertificationCell(parameters.listViewParams.urls, {
      isCertified: (row: DataGrid.Row) => hasCertification(row.data as IVirtualConnection),
      getTooltipProps: (row: DataGrid.Row, urls: AppUrls): CertificationWatermark.CertificationTooltipProps => {
        return generatePublishedConnectionCertificationTooltipProps(row, parameters.listViewParams.urls);
      },
    });
    const linkCellFactory = LinkCell(
      parameters.generateLinkForName,
      true,
      parameters.listViewParams.urls,
      parameters.listViewParams.searchTerm,
      parameters.listViewParams.total
    );

    const dataQualityIndicatorFactory = DataQualityWarningCell(parameters.listViewParams.urls, (row) => {
      return row.data.dataQualityIndicators && row.data.dataQualityIndicators[0] && row.data.dataQualityIndicators[0].isActive;
    });
    return (
      <div className={Layout.flexH} style={{ width: '100%' }}>
        <div className={IconStyle}>{contentTypeCellFactory(props)}</div>
        <div className={IconStyle}>{certificationFactory(props)}</div>
        <div className={IconStyle}>{dataQualityIndicatorFactory(props)}</div>
        <div className={Layout.flexAuto} style={{ overflow: 'hidden' }}>
          {linkCellFactory(props)}
        </div>
      </div>
    );
  });
}

function generatePublishedConnectionCertificationTooltipProps(
  row: DataGrid.Row,
  urls: AppUrls
): CertificationWatermark.CertificationTooltipProps {
  const contentDetail = row.data.contentDetail || row.data;
  const dqiDetails = row.data.dataQualityIndicators.find((dqi: IDataQualityIndicatorInfo) => dqi.type === CERTIFIED_INDICATOR_TYPE);
  const certifier = UserMapper.mapOne(dqiDetails.owner);
  const certificationNote = dqiDetails.message ? dqiDetails.message : '';
  const certificationDetails = { certificationNote: certificationNote, certifier: contentDetail.certifier, isCertified: dqiDetails };
  return (
    certificationDetails && {
      id: row.data.id,
      certifier: certifier && {
        customUserStyle: certifier.isHiddenInternalUser ? (HiddenUserStyles.HiddenUserStyle as React.CSSProperties) : undefined,
        displayName: certifier.displayName,
        url: certifier.domainName ? urls.userUrl(certifier.username, certifier.domainName) : '',
      },
      note: certificationDetails.certificationNote,
      date: MapperHelper.toDate(contentDetail.lastRefreshedAt),
    }
  );
}

const CERTIFIED_INDICATOR_TYPE = 'CERTIFIED';
function hasCertification(contentItem: IVirtualConnection): boolean {
  return (
    contentItem.dataQualityIndicators &&
    contentItem.dataQualityIndicators.some((dqi) => dqi.isActive && dqi.type === CERTIFIED_INDICATOR_TYPE)
  );
}
